import * as React from "react";

/**
 * Loose-coupling mediator between the canonical docked drawer (`dialog.tsx`) and the left-nav
 * sidebar (`sidebar.tsx`) — ENT-3477.
 *
 * The 100% drawer preset must condense the left menu to its icon rail and fill the remaining
 * width, then restore the menu on close / 50% without ever mutating the user's saved
 * `sidebar:state`. To keep the two components decoupled (dialog.tsx and sidebar.tsx MUST NOT
 * import each other — only this module), the drawer raises a generic, ref-counted "please render
 * condensed" REQUEST here; the sidebar consults it when deciding what to draw. Neither knows the
 * other exists. On a surface with no `SidebarProvider` the drawer's `useOptionalMenuCondense()`
 * returns null and it behaves exactly as before (full-viewport 100%, no crash).
 *
 * The request is a presentation-layer overlay, NOT a state mutation: the sidebar's real `open`
 * value and cookie are untouched, so "restore" = drop the request = reveal the state that was
 * never changed. That is what makes retention bulletproof across reloads (the cookie still says
 * whatever the user last chose) with zero persistence code.
 */
export interface MenuCondenseContextValue {
  /** True while at least one drawer is holding a condense request. */
  condenseRequested: boolean;
  /** Acquire a condense request; returns a release function (idempotent). Ref-counted, so
   *  overlapping drawers keep the menu condensed until the last release. */
  requestCondense: () => () => void;
  /** Horizontal space the collapsed menu still occupies, in px (icon rail width on desktop, 0 on
   *  mobile / off-canvas shells). The drawer uses it as `fullInsetPx` so its 100% width lands flush
   *  against the rail. */
  condensedInsetPx: number;
  /** Monotonic counter the sidebar bumps when the user manually expands the menu while a request is
   *  held (R10.1). Holders react to CHANGES, never to the absolute value. */
  expandDemandSeq: number;
}

const MenuCondenseContext = React.createContext<MenuCondenseContextValue | null>(null);

/** Context provider element — used ONLY by `SidebarProvider`. */
export const MenuCondenseProvider = MenuCondenseContext.Provider;

/**
 * Own the condense request set. Used ONLY by `SidebarProvider`. Returns the context `value` to
 * publish plus a `bumpExpandDemand` the provider calls from its toggle interception (R10.1) — the
 * bump is intentionally NOT on the public context value, since only the sidebar may fire it.
 */
export function useProvideMenuCondense(options: { condensedInsetPx: number }): {
  value: MenuCondenseContextValue;
  bumpExpandDemand: () => void;
} {
  const { condensedInsetPx } = options;
  const [requestCount, setRequestCount] = React.useState(0);
  const [expandDemandSeq, setExpandDemandSeq] = React.useState(0);

  const requestCondense = React.useCallback(() => {
    setRequestCount((count) => count + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setRequestCount((count) => Math.max(0, count - 1));
    };
  }, []);

  const bumpExpandDemand = React.useCallback(() => {
    setExpandDemandSeq((seq) => seq + 1);
  }, []);

  const value = React.useMemo<MenuCondenseContextValue>(
    () => ({
      condenseRequested: requestCount > 0,
      requestCondense,
      condensedInsetPx,
      expandDemandSeq,
    }),
    [requestCount, requestCondense, condensedInsetPx, expandDemandSeq],
  );

  return { value, bumpExpandDemand };
}

/**
 * Read the mediator from the drawer side. Used ONLY by the drawer. Returns the context value, or
 * `null` when there is no `SidebarProvider` above (never throws) — so the drawer no-ops cleanly on
 * provider-less surfaces and Storybook stories.
 */
export function useOptionalMenuCondense(): MenuCondenseContextValue | null {
  return React.useContext(MenuCondenseContext);
}
